import { useState } from "react";
import type { CanonStatus, Character, LifeStatus, StatePatch } from "../domain/types";
import {
  characterTrajectory,
  characterKnows,
  getCharacter,
  getMoment,
  relationshipsAt,
  resolveCharacterState,
} from "../domain/engine";
import { Field, LIFE, Modal, SearchBox, SourceFields, Sources, StatusBadge, filterText, nowIso, submitGuard } from "../components/ui";
import { useStore } from "../store";
import { useHash } from "../components/ui";

export function Characters({ characterId }: { characterId?: string }) {
  const { bible, selection } = useStore();
  const [, navigate] = useHash();
  const [q, setQ] = useState("");
  if (!bible) return null;
  const list = bible.characters.filter((c) =>
    filterText(`${c.name} ${c.className} ${c.role} ${c.gift}`, q),
  );
  const current = characterId ? getCharacter(bible, characterId) : null;

  if (current) {
    return (
      <CharacterSheet
        character={current}
        onBack={() => navigate("/personnages")}
      />
    );
  }

  return (
    <div>
      <h1>Personnages</h1>
      <SearchBox value={q} onChange={setQ} placeholder="Rechercher un personnage" />
      <div className="grid grid-2" style={{ marginTop: 12 }}>
        {list.map((c) => {
          const st = resolveCharacterState(bible, c.id, selection.momentId);
          return (
            <button key={c.id} className="card" type="button" onClick={() => navigate(`/personnages/${c.id}`)} style={{ textAlign: "left" }}>
              <div className="row space">
                <h3>{c.name}</h3>
                <StatusBadge status={c.status} />
              </div>
              <p className="tiny">{c.className}</p>
              <p className="tiny">{selection.momentId ? `${st?.lifeStatus ?? ""} ${st?.rank ? `· ${st.rank}e` : ""} ${st?.location ?? ""}` : "Vue générale — trajectoire, pas d’état « actuel »"}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CharacterSheet({ character, onBack }: { character: Character; onBack: () => void }) {
  const { bible, world, selection, updateBible } = useStore();
  const [patchOpen, setPatchOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  if (!bible) return null;
  const state = resolveCharacterState(bible, character.id, selection.momentId);
  const trajectory = characterTrajectory(bible, character.id);
  const rels = relationshipsAt(bible, selection.momentId, character.id);
  const goals = world.rankingGoals.filter((g) => g.characterId === character.id);
  const fragment = world.fragments.find((f) => f.bearerId === character.id);
  const moment = getMoment(bible, selection.momentId);
  const magdaNote = character.id === "magda";
  const bjornNote = character.id === "bjorn";

  return (
    <div className="grid">
      <button className="btn" type="button" onClick={onBack}>← Liste</button>
      <div className="row space">
        <h1>{character.name}</h1>
        <div className="row">
          <StatusBadge status={character.status} />
          <button className="btn" type="button" onClick={() => setEditOpen(true)}>Modifier la fiche générale</button>
          <button className="btn primary" type="button" onClick={() => setPatchOpen(true)} disabled={!selection.momentId}>
            Changement temporel
          </button>
        </div>
      </div>
      <section className="card">
        <h2>Identité</h2>
        <p>{character.summary}</p>
        <p className="tiny">Rôle : {character.role} · Origine : {character.origin} · Famille : {character.family?.join(", ")}</p>
        <p>{character.appearance}</p>
        <p>Traits : {character.traits?.join(" · ")}</p>
        <Sources sources={character.sources} />
      </section>
      <section className="card">
        <h2>{selection.momentId ? `État au moment ${moment?.label}` : "Vue générale"}</h2>
        {!selection.momentId && (
          <p className="warn tiny">Aucun filtre temporel. Ci-dessous : données générales + historique. Rien n’est présenté comme « l’état actuel ».</p>
        )}
        <div className="grid grid-2">
          <p><strong>Classe</strong><br />{state?.className ?? character.className}</p>
          <p><strong>Don</strong><br />{character.gift}</p>
          <p><strong>Statut de vie</strong><br />{selection.momentId ? state?.lifeStatus : "voir l’historique"}</p>
          <p><strong>Rang atteint</strong><br />{selection.momentId ? (state?.rank ?? "non renseigné") : "selon les moments"}</p>
          <p><strong>Guilde</strong><br />{state?.guild ?? "—"}</p>
          <p><strong>Lieu</strong><br />{state?.location ?? "—"}</p>
          <p><strong>Physique</strong><br />{state?.physicalState ?? "—"}</p>
          <p><strong>Mental</strong><br />{state?.psychologicalState ?? "—"}</p>
        </div>
        {bjornNote && (
          <p className="tiny">Canon : Sorcier spécialisé Foudre. « Guerrier de Foudre » n’est qu’une ancienne formulation, signalée en continuité.</p>
        )}
        {magdaNote && (
          <p className="tiny">Aucune limite générale de copie n’est connue, y compris par Magda elle-même. Les surcharges observées restent des événements, pas une règle.</p>
        )}
        {goals.length > 0 && (
          <div>
            <h3>Objectifs de classement (non atteints)</h3>
            <ul className="tiny">
              {goals.map((g) => (
                <li key={g.bookId}>{g.label} — {g.bookId}</li>
              ))}
            </ul>
          </div>
        )}
        {fragment && (
          <p>Fragment : {fragment.name ?? "nom non confirmé — ne pas inventer"} — {fragment.description}</p>
        )}
        <p>Capacités : {character.abilities?.join(" · ")}</p>
      </section>
      <section className="card">
        <h2>Relations actives {selection.momentId ? "à cet instant" : "(toutes les phases)"}</h2>
        {rels.map((r) => (
          <p key={r.id}><strong>{r.label}</strong> ({r.type}) — {r.summary} <StatusBadge status={r.status} /></p>
        ))}
        {!rels.length && <p className="tiny">Aucune relation dans cette lentille.</p>}
      </section>
      <section className="card">
        <h2>Secrets connus</h2>
        {bible.secrets.map((s) => {
          const k = characterKnows(bible, s, character.id, selection.momentId);
          if (!k && selection.momentId) return null;
          if (!k) return (
            <p key={s.id} className="tiny">{s.title} — pas de connaissance structurée dans la vue générale.</p>
          );
          return (
            <p key={s.id}><strong>{s.title}</strong> · {k.certainty} depuis {k.knownFromMomentId}</p>
          );
        })}
      </section>
      <section className="card">
        <h2>Historique des changements</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Moment</th>
                <th>Branche</th>
                <th>Changements</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {trajectory.map((p) => (
                <tr key={p.id}>
                  <td>{p.validFromMomentId}{p.validToMomentId ? ` → ${p.validToMomentId}` : ""}</td>
                  <td>{p.branchId}{p.loopIterationId ? ` · ${p.loopIterationId}` : ""}</td>
                  <td className="tiny">
                    {p.className ? `Classe ${p.className}. ` : ""}
                    {p.rank != null ? `Rang ${p.rank}. ` : ""}
                    {p.lifeStatus ? `Vie ${p.lifeStatus}. ` : ""}
                    {p.physicalState ? p.physicalState + ". " : ""}
                    {p.location ? `Lieu ${p.location}. ` : ""}
                    {p.notes}
                  </td>
                  <td><StatusBadge status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <PatchForm
        open={patchOpen}
        character={character}
        defaultMomentId={selection.momentId}
        defaultBranchId={moment?.branchId ?? "branch:main"}
        defaultLoop={moment?.loopIterationId ?? null}
        onClose={() => setPatchOpen(false)}
        onCreate={(patch) => {
          updateBible((draft) => ({
            ...draft,
            characters: draft.characters.map((c) =>
              c.id === character.id ? { ...c, states: [...c.states, patch], updatedAt: nowIso() } : c,
            ),
          }), `Changement temporel ${character.name}`);
          setPatchOpen(false);
        }}
      />
      <EditGeneral
        open={editOpen}
        character={character}
        onClose={() => setEditOpen(false)}
        onSave={(next) => {
          updateBible((draft) => ({
            ...draft,
            characters: draft.characters.map((c) => (c.id === character.id ? next : c)),
          }), `Fiche générale ${character.name}`);
          setEditOpen(false);
        }}
      />
    </div>
  );
}

function PatchForm({
  open,
  character,
  defaultMomentId,
  defaultBranchId,
  defaultLoop,
  onClose,
  onCreate,
}: {
  open: boolean;
  character: Character;
  defaultMomentId: string | null;
  defaultBranchId: string;
  defaultLoop: string | null;
  onClose: () => void;
  onCreate: (p: StatePatch) => void;
}) {
  const [status, setStatus] = useState<CanonStatus>("draft");
  const [sourceLabel, setSourceLabel] = useState("");
  const [lifeStatus, setLifeStatus] = useState("");
  const [physicalState, setPhysicalState] = useState("");
  const [psychologicalState, setPsychologicalState] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  return (
    <Modal open={open} title={`Changement — ${character.name}`} onClose={onClose}>
      <form
        className="grid"
        onSubmit={(e) => {
          if (!submitGuard(e, status, sourceLabel) || !defaultMomentId) return;
          onCreate({
            id: `${character.id}-state-${Date.now().toString(36)}`,
            status,
            sources: sourceLabel ? [{ label: sourceLabel }] : [],
            validFromMomentId: defaultMomentId,
            branchId: defaultBranchId,
            loopIterationId: defaultLoop,
            lifeStatus: (lifeStatus || undefined) as LifeStatus | undefined,
            physicalState: physicalState || undefined,
            psychologicalState: psychologicalState || undefined,
            location: location || undefined,
            notes,
          });
        }}
      >
        <p className="tiny">Seuls les champs remplis seront appliqués à partir du moment sélectionné. Les autres branches restent intactes.</p>
        <Field label="Statut de vie">
          <select value={lifeStatus} onChange={(e) => setLifeStatus(e.target.value)}>
            <option value="">(aucun changement)</option>
            {LIFE.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </Field>
        <Field label="État physique"><input value={physicalState} onChange={(e) => setPhysicalState(e.target.value)} /></Field>
        <Field label="État psychologique"><input value={psychologicalState} onChange={(e) => setPsychologicalState(e.target.value)} /></Field>
        <Field label="Lieu"><input value={location} onChange={(e) => setLocation(e.target.value)} /></Field>
        <Field label="Notes"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} /></Field>
        <SourceFields status={status} sourceLabel={sourceLabel} setStatus={setStatus} setSourceLabel={setSourceLabel} />
        <button className="btn primary" type="submit">Ajouter</button>
      </form>
    </Modal>
  );
}

function EditGeneral({
  open,
  character,
  onClose,
  onSave,
}: {
  open: boolean;
  character: Character;
  onClose: () => void;
  onSave: (c: Character) => void;
}) {
  const [summary, setSummary] = useState(character.summary);
  const [appearance, setAppearance] = useState(character.appearance ?? "");
  const [status, setStatus] = useState<CanonStatus>(character.status);
  const [sourceLabel, setSourceLabel] = useState(character.sources[0]?.label ?? "");
  return (
    <Modal open={open} title="Fiche générale" onClose={onClose}>
      <form
        className="grid"
        onSubmit={(e) => {
          if (!submitGuard(e, status, sourceLabel)) return;
          onSave({
            ...character,
            summary,
            appearance,
            status,
            sources: sourceLabel ? [{ label: sourceLabel }] : character.sources,
            updatedAt: nowIso(),
          });
        }}
      >
        <p className="tiny">Ceci modifie la fiche durable, pas un état daté. Pour un changement à un chapitre, utilisez « Changement temporel ».</p>
        <Field label="Résumé"><textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={5} /></Field>
        <Field label="Apparence"><textarea value={appearance} onChange={(e) => setAppearance(e.target.value)} rows={3} /></Field>
        <SourceFields status={status} sourceLabel={sourceLabel} setStatus={setStatus} setSourceLabel={setSourceLabel} />
        <button className="btn primary" type="submit">Enregistrer</button>
      </form>
    </Modal>
  );
}
